import React, { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import YearPicker from './YearPicker';
import './ReportingControls.css';

/**
 * Contexte de reporting global : année affichée (activeYear) + dernier mois
 * réel clôturé (tenant.last_closed_period). Fourni par App.js autour des pages,
 * consommé par la BARRE SUPÉRIEURE (AppTopBar) depuis le 2026-08-18 : exercice
 * et frontière réel/prévision se lisent une seule fois, à côté du client, et
 * non plus sur la ligne de titre de chaque page.
 *
 * Forme : { year, onYearChange, closed }
 *   - closed : "YYYY-MM-01" | null (état tenant), en LECTURE — la clôture
 *     s'édite dans Finance › Paramètres › Clôture (décision comptable).
 */
export const ReportingContext = createContext(null);

/* La VERSION DE PLAN que lit l'écran (celle de la bande de contexte, sinon la
   courante), et les années planifiées après `year` jusqu'à sa dernière année :
   l'horizon appartient à la version depuis le 2026-10-11. */
export function useReadVersion() {
  const ctx = useContext(ReportingContext);
  const versions = (ctx && ctx.versions) || [];
  return versions.find((v) => v.id === (ctx && ctx.versionId)) || versions.find((v) => v.is_current) || null;
}
export function horizonAfter(version, year, defaut = 3) {
  if (!version || version.start_year == null) return defaut;
  return Math.max(0, version.start_year + version.horizon_years - Number(year));
}

export default function ReportingControls() {
  const p = useP();
  const ctx = useContext(ReportingContext);
  if (!ctx) return null;   // hors provider (ex. documents d'impression) → rien

  const { year, onYearChange } = ctx;
  // Le dernier mois réel clôturé n'est plus affiché ici : c'est une
  // INFORMATION, elle vit désormais en pastille sur la ligne du titre
  // (<ClosedChip />, rendue par PageHeader) — à gauche avec le contexte fixe,
  // les commandes de page restant seules à droite (décision 2026-08-16).
  return (
    <div className="reporting-controls">
      <YearPicker year={year} onYearChange={onYearChange} label={p('Exercice ')} />
    </div>
  );
}

/* Pastille d'état « Réel · juin 2026 » — l'information d'abord (la clôture
   s'édite dans Paramètres › Finance). Pour qui a le droit de la modifier,
   `onEdit` en fait un lien vers ce réglage (demande d'Olivier, 2026-10-12). */
export function ClosedChip({ onEdit = null }) {
  const { t, i18n } = useTranslation();
  const p = useP();
  const ctx = useContext(ReportingContext);
  if (!ctx || !ctx.closed) return null;
  const { closed } = ctx;
  /* L'année ne se dit que si elle diffère de l'exercice lu (2026-09-16) : le
     statut voisine désormais ‹ 2026 ›, « Réel · Mar 2026 · ‹ 2026 › » la
     répétait ; « Réel · Jul 2025 » face à ‹ 2026 › reste dit en entier. */
  const closedYear = closed.slice(0, 4);
  /* EN TOUTES LETTRES DANS LA BARRE SUPÉRIEURE (2026-09-30) : « Réel à fin
     juillet » se lit comme une information, plus comme une pastille. Le mois
     vient du navigateur, dans la langue de l'interface. */
  const month = new Date(Number(closedYear), Number(closed.slice(5, 7)) - 1, 1)
    .toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-CH', { month: 'long' });
  const label = String(ctx.year) === closedYear ? month : `${month} ${closedYear}`;
  if (onEdit) {
    return (
      <button type="button" className="reporting-closed-chip reporting-closed-chip--lien"
              title={p('Modifier la clôture dans Paramètres')} onClick={onEdit}>
        {p('Réel à fin')} {label}
      </button>
    );
  }
  return (
    <span className="reporting-closed-chip" title={t('phrases:p_fff4b9014c')}>
      {p('Réel à fin')} {label}
    </span>
  );
}
